import { mergeSimulateResultIntoProposalData } from './merge-simulate-result';
import {
  getProductionPerPanelResult,
  getProductionPerStringResult
} from '../production/production-mapping';

export const isUuid = (value) =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value
  );

export const buildSyntheticStringingFromSelectedPanels = (
  selectedPanelsPerRoof = null
) => {
  if (!selectedPanelsPerRoof || typeof selectedPanelsPerRoof !== 'object') {
    return null;
  }

  const pairs = [];
  Object.keys(selectedPanelsPerRoof).forEach((roofKey) => {
    const segmentIndex = Number(roofKey);
    if (!Number.isFinite(segmentIndex)) return;
    const roofPanels = Array.isArray(selectedPanelsPerRoof[roofKey])
      ? selectedPanelsPerRoof[roofKey]
      : [];
    roofPanels.forEach((panelIndexValue) => {
      const panelIndex = Number(panelIndexValue);
      if (!Number.isFinite(panelIndex)) return;
      pairs.push([segmentIndex, panelIndex]);
    });
  });

  return pairs.length > 0 ? [{ panels: pairs, draw: false }] : null;
};

export const getPanelAxisForDesign = (
  designItem = {},
  roofIndex = 0,
  designData = {}
) => {
  const fromDesign =
    designItem.panelAxis ??
    designItem.panel_axis ??
    designData?.designs?.find((design) => design?.id === designItem.id)
      ?.panel_axis ??
    null;
  if (fromDesign) return fromDesign;

  const mappedSegmentId =
    designData?.design_map?.[designItem.id] ??
    designData?.designMap?.[designItem.id] ??
    null;
  const segments = Array.isArray(designData?.segments)
    ? designData.segments
    : [];
  const segment = mappedSegmentId
    ? segments.find(
        (segmentItem) => String(segmentItem?.id) === String(mappedSegmentId)
      )
    : segments[roofIndex];
  return segment?.panelAxis ?? segment?.panel_axis ?? null;
};

export const buildDesignsPayloadWithStringing = ({
  design = [],
  selectedPanels = {},
  strings = null,
  designData = {}
} = {}) =>
  (Array.isArray(design) ? design : []).map((designItem, roofIndex) => {
    const { id, vertical = 0, horizontal = 0 } = designItem || {};
    const total = Number(vertical || 0) + Number(horizontal || 0);
    const selected = Array.isArray(selectedPanels?.[id])
      ? selectedPanels[id]
      : Array(total).fill(0);
    const verticalSelected = selected.slice(0, Number(vertical || 0));
    const horizontalSelected = selected.slice(Number(vertical || 0));
    const roofStrings = Array.isArray(strings)
      ? strings.filter(
          (string) =>
            Array.isArray(string?.panels) &&
            string.panels.some(
              (panel) => Array.isArray(panel) && panel[0] === roofIndex
            )
        )
      : [];

    return {
      id,
      vertical_selected: verticalSelected.join(','),
      horizontal_selected: horizontalSelected.join(','),
      panel_axis: getPanelAxisForDesign(designItem, roofIndex, designData),
      stringing_data: roofStrings
    };
  });

export const buildDesignDataWithLiveProduction = ({
  baseDesignData = {},
  production = {},
  strings
} = {}) => {
  const productionPerPanel = Array.isArray(production.productionPerPanelData)
    ? production.productionPerPanelData
    : [];
  const productionPerString = Array.isArray(production.productionPerStringData)
    ? production.productionPerStringData
    : [];
  const resolvedStrings =
    Array.isArray(strings) || strings === null
      ? strings
      : production.stringingData;

  return {
    ...baseDesignData,
    monthlyProduction:
      baseDesignData?.monthlyProduction ?? production.monthlyProduction ?? [],
    stringing_panel_index_mode:
      production.stringingPanelIndexMode ||
      baseDesignData?.stringing_panel_index_mode ||
      'all',
    dc_ac_ratio: production.dcAcRatio ?? baseDesignData?.dc_ac_ratio ?? null,
    annual_clipping_kwh:
      production.clippingKwh ?? baseDesignData?.annual_clipping_kwh ?? 0,
    monthly_clipping_kwh:
      production.monthlyClippingKwh ??
      baseDesignData?.monthly_clipping_kwh ??
      Array(12).fill(0),
    productionPerPanel,
    production_per_panel: productionPerPanel,
    productionPerString,
    production_per_string: productionPerString,
    stringing_data: resolvedStrings || null,
    inverters_used:
      production.invertersUsed ?? baseDesignData?.inverters_used ?? null,
    mppt_count_total:
      production.mpptCountTotal ?? baseDesignData?.mppt_count_total ?? null
  };
};

export const buildReportProductionPersistence = ({
  designData = {},
  design = [],
  selectedPanels = {},
  strings = null,
  result = {},
  annualKwh = 0,
  monthlyKwh = [],
  systemSize = 0,
  panelCount = 0,
  offset = 0,
  stringingPanelIndexMode = 'all',
  productionPerPanel = null,
  productionPerString = null,
  inverter = null,
  inverterQuantity = 1,
  primaryInverterAutoQuantity = true,
  hasAutoCalculatedInverterQuantity = false,
  additionalInverters = [],
  limitReached = false
} = {}) => {
  const resolvedProductionPerPanel = Array.isArray(productionPerPanel)
    ? productionPerPanel
    : getProductionPerPanelResult(result);
  const resolvedProductionPerString = Array.isArray(productionPerString)
    ? productionPerString
    : getProductionPerStringResult(result);
  const designs = buildDesignsPayloadWithStringing({
    design,
    selectedPanels,
    strings,
    designData
  });
  const merged = mergeSimulateResultIntoProposalData(
    { designs, design_data: designData },
    {
      ...result,
      annual_kwh: annualKwh,
      monthly_kwh: monthlyKwh,
      system_size_kw: systemSize,
      panel_count: panelCount,
      resolved_strings: strings,
      production_per_panel: resolvedProductionPerPanel,
      production_per_string: resolvedProductionPerString
    }
  );
  const mergedDesignData = merged?.design_data || {};
  const productionInvertersUsed =
    result?.solar_design?.[0]?.inverters_used || null;

  return {
    designs,
    design_data: {
      ...designData,
      inverter,
      inverterQuantity,
      inverter_quantity: inverterQuantity,
      primaryInverterAutoQuantity,
      hasAutoCalculatedInverterQuantity,
      additional_inverters: additionalInverters,
      summary: {
        ...(mergedDesignData.summary || designData?.summary || {}),
        solar_production: annualKwh,
        system_size: systemSize?.toFixed ? systemSize.toFixed(3) : systemSize,
        total_panels: panelCount,
        offset: Math.round(offset || 0)
      },
      systemSize: systemSize ?? 0,
      system_size: systemSize ?? 0,
      monthlyProduction: mergedDesignData.monthlyProduction ?? monthlyKwh,
      stringing_panel_index_mode: stringingPanelIndexMode,
      dc_ac_ratio: mergedDesignData.dc_ac_ratio ?? null,
      annual_clipping_kwh: result?.annual_clipping_kwh ?? 0,
      monthly_clipping_kwh: result?.monthly_clipping_kwh || Array(12).fill(0),
      productionPerPanel: resolvedProductionPerPanel,
      production_per_panel: resolvedProductionPerPanel,
      productionPerString: resolvedProductionPerString,
      production_per_string: resolvedProductionPerString,
      stringing_data: mergedDesignData.stringing_data ?? strings ?? null,
      inverters_used: productionInvertersUsed,
      limitReached: Boolean(limitReached),
      limit_reached: Boolean(limitReached)
    }
  };
};

export const buildShadingValuesPerRoof = (design = []) =>
  (Array.isArray(design) ? design : []).map(
    (
      { annualAccess, annualTSRF, annualTOF, solarAccess, TSRF, TOF },
      index
    ) => ({
      idx: index,
      annualSA: annualAccess,
      annualTSRF,
      annualTOF,
      monthlySA: solarAccess,
      monthlyTSRF: TSRF,
      monthlyTOF: TOF
    })
  );

export const buildRoofOrientationValues = (design = []) =>
  (Array.isArray(design) ? design : []).map(({ azimuth, pitch }, index) => ({
    idx: index,
    azimuth,
    pitch
  }));

export const buildProductionPerPanelValues = ({
  design = [],
  selectedPanels = {},
  annualProduction = 0
} = {}) => {
  const selectedCount = Object.values(selectedPanels || {}).reduce(
    (sum, values) =>
      sum +
      (Array.isArray(values)
        ? values.filter((value) => value === 1).length
        : 0),
    0
  );
  const productionPerSelectedPanel =
    selectedCount > 0 ? Number(annualProduction || 0) / selectedCount : 0;

  return (Array.isArray(design) ? design : []).map((segment, index) => {
    const selected = selectedPanels?.[segment?.id] || [];
    return {
      idx: index,
      roofPanels: Array.isArray(selected)
        ? selected.map((value) =>
            value === 1 ? productionPerSelectedPanel : 0
          )
        : []
    };
  });
};
